import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { UserPlus, KeyRound, Trash2, Shield, UserCog, Building2, Plus, X } from "lucide-react";

const ROLE_LABELS = {
  super_admin: "Super Admin", admin_store: "Amministratore sede",
  operatore: "Operatore", viewer: "Visualizzatore",
};

export default function Utenti() {
  const { stores } = useStore();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ username: "", name: "", email: "", role: "operatore", password: "", store_ids: [] });
  const [resetFor, setResetFor] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [assignFor, setAssignFor] = useState(null);
  const [assign, setAssign] = useState({ store_id: "", role: "operatore" });

  const storeName = (id) => stores.find((s) => s.id === id)?.name || id;

  const load = useCallback(async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await api.post("/users", form);
      toast.success("Utente creato (cambio password obbligatorio al primo accesso)");
      setOpen(false);
      setForm({ username: "", name: "", email: "", role: "operatore", password: "", store_ids: [] });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const resetPw = async () => {
    try {
      await api.post(`/users/${resetFor.id}/reset-password`, { new_password: newPw });
      toast.success("Password reimpostata"); setResetFor(null); setNewPw("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Eliminare l'utente ${u.username}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Utente eliminato"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const changeRole = async (u, role) => {
    try { await api.put(`/users/${u.id}`, { role }); toast.success("Ruolo aggiornato"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const addAssignment = async () => {
    try {
      await api.post("/assignments", { user_id: assignFor.id, store_id: assign.store_id, role: assign.role });
      toast.success("Assegnazione salvata"); setAssignFor(null); setAssign({ store_id: "", role: "operatore" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const removeAssignment = async (aid) => {
    try { await api.delete(`/assignments/${aid}`); load(); if (assignFor) { const u = users.find(x => x.id === assignFor.id); } }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const toggleStoreInForm = (id) =>
    setForm((f) => ({ ...f, store_ids: f.store_ids.includes(id) ? f.store_ids.filter((x) => x !== id) : [...f.store_ids, id] }));

  const assignFptUser = assignFor ? users.find((u) => u.id === assignFor.id) : null;

  return (
    <div className="space-y-6" data-testid="utenti-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl">Gestione Utenti</h1>
          <p className="text-sm text-muted-foreground">Ruoli e assegnazione alle sedi. I permessi sono verificati lato server.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="gap-2 font-bold" data-testid="add-user-btn"><UserPlus className="h-4 w-4" /> Nuovo utente</Button></DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="add-user-dialog">
            <DialogHeader><DialogTitle>Nuovo utente</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="mb-1 block text-xs font-semibold uppercase text-muted-foreground">Username</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="h-11" data-testid="user-username-input" /></div>
              <div><Label className="mb-1 block text-xs font-semibold uppercase text-muted-foreground">Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11" data-testid="user-name-input" /></div>
              <div>
                <Label className="mb-1 block text-xs font-semibold uppercase text-muted-foreground">Ruolo</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="h-11" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="super_admin">Super Admin (tutte le sedi)</SelectItem>
                    <SelectItem value="admin_store">Amministratore sede</SelectItem>
                    <SelectItem value="operatore">Operatore</SelectItem>
                    <SelectItem value="viewer">Visualizzatore (sola lettura)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.role !== "super_admin" && (
                <div>
                  <Label className="mb-1 block text-xs font-semibold uppercase text-muted-foreground">Sedi assegnate</Label>
                  <div className="flex flex-wrap gap-2">
                    {stores.map((s) => (
                      <button type="button" key={s.id} onClick={() => toggleStoreInForm(s.id)}
                        className={`rounded-full border px-3 py-1.5 text-xs font-medium ${form.store_ids.includes(s.id) ? "border-primary bg-primary/20 text-primary" : "border-border text-muted-foreground"}`}>
                        {s.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div><Label className="mb-1 block text-xs font-semibold uppercase text-muted-foreground">Password provvisoria</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-11" data-testid="user-password-input" /></div>
            </div>
            <DialogFooter><Button onClick={create} className="font-bold" data-testid="user-create-submit-btn">Crea utente</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="divide-y divide-border/50 rounded-xl border border-border bg-card">
        {users.map((u) => (
          <div key={u.id} className="px-5 py-4" data-testid={`user-row-${u.username}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${u.role === "super_admin" ? "bg-primary/20 text-primary" : "bg-secondary text-muted-foreground"}`}>
                  {u.role === "super_admin" ? <Shield className="h-5 w-5" /> : <UserCog className="h-5 w-5" />}
                </div>
                <div>
                  <div className="font-semibold">{u.name || u.username} <span className="text-muted-foreground">@{u.username}</span></div>
                  <div className="text-xs uppercase tracking-wide text-primary">{ROLE_LABELS[u.role]}{u.must_change_password && <span className="ml-2 text-amber-400">· cambio pw</span>}</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                  <SelectTrigger className="h-9 w-[150px]" data-testid={`role-select-${u.username}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                    <SelectItem value="admin_store">Admin sede</SelectItem>
                    <SelectItem value="operatore">Operatore</SelectItem>
                    <SelectItem value="viewer">Visualizzatore</SelectItem>
                  </SelectContent>
                </Select>
                {u.role !== "super_admin" && <Button variant="ghost" size="sm" className="gap-1" onClick={() => setAssignFor(u)} data-testid={`assign-${u.username}`}><Building2 className="h-4 w-4" /></Button>}
                <Button variant="ghost" size="sm" className="gap-1" onClick={() => setResetFor(u)} data-testid={`reset-pw-${u.username}`}><KeyRound className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" className="text-destructive" onClick={() => remove(u)} data-testid={`delete-user-${u.username}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
            {u.role !== "super_admin" && u.assignments?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5 pl-13">
                {u.assignments.map((a) => (
                  <span key={a.id} className="flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground" data-testid={`asg-${u.username}-${a.store_id}`}>
                    {storeName(a.store_id)} · {a.role}
                    <button onClick={() => removeAssignment(a.id)} className="text-destructive"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* reset pw */}
      <Dialog open={!!resetFor} onOpenChange={(o) => !o && setResetFor(null)}>
        <DialogContent data-testid="reset-pw-dialog">
          <DialogHeader><DialogTitle>Reset password · {resetFor?.username}</DialogTitle></DialogHeader>
          <Label className="mb-1 block text-xs font-semibold uppercase text-muted-foreground">Nuova password provvisoria</Label>
          <Input value={newPw} onChange={(e) => setNewPw(e.target.value)} className="h-11" data-testid="reset-pw-input" />
          <DialogFooter><Button onClick={resetPw} className="font-bold" data-testid="reset-pw-submit-btn">Reimposta</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* assignment */}
      <Dialog open={!!assignFor} onOpenChange={(o) => !o && setAssignFor(null)}>
        <DialogContent data-testid="assign-dialog">
          <DialogHeader><DialogTitle>Assegna sede · {assignFor?.username}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1 block text-xs font-semibold uppercase text-muted-foreground">Punto vendita</Label>
              <Select value={assign.store_id} onValueChange={(v) => setAssign({ ...assign, store_id: v })}>
                <SelectTrigger className="h-11" data-testid="assign-store-select"><SelectValue placeholder="Scegli" /></SelectTrigger>
                <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1 block text-xs font-semibold uppercase text-muted-foreground">Ruolo sulla sede</Label>
              <Select value={assign.role} onValueChange={(v) => setAssign({ ...assign, role: v })}>
                <SelectTrigger className="h-11" data-testid="assign-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin_store">Amministratore sede</SelectItem>
                  <SelectItem value="operatore">Operatore</SelectItem>
                  <SelectItem value="viewer">Visualizzatore</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={addAssignment} disabled={!assign.store_id} className="gap-2 font-bold" data-testid="assign-submit-btn"><Plus className="h-4 w-4" /> Assegna</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
